import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Trophy, Calendar, Info, Home, Settings } from "lucide-react";

// Pages
import HomePage from "@/pages/HomePage";
import FixturesPage from "@/pages/FixturesPage";
import EnterResultPage from "@/pages/EnterResultPage";
import InfoPage from "@/pages/InfoPage";
import AdminPage from "@/pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with cache busting
const apiClient = axios.create({
  baseURL: API,
});

// Add cache-busting interceptor
apiClient.interceptors.request.use((config) => {
  // Add timestamp to prevent caching
  const timestamp = Date.now();
  config.params = {
    ...config.params,
    _t: timestamp,
  };
  // Add cache-control headers
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';
  return config;
});

export { apiClient };

// Context for app-wide state
export const AppContext = createContext();

export const useApp = () => useContext(AppContext);

// Bottom Navigation Component
const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = [
    { path: "/", icon: Home, label: "Table" },
    { path: "/fixtures", icon: Calendar, label: "Matches" },
    { path: "/enter-result", icon: Trophy, label: "Enter" },
    { path: "/info", icon: Info, label: "Info" },
    { path: "/admin", icon: Settings, label: "Admin" },
  ];

  return (
    <nav 
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[1200px] glass border-t border-border z-50"
      data-testid="bottom-navigation"
    >
      <div className="flex justify-around items-center max-w-lg mx-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`nav-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon 
                size={22} 
                className={isActive ? "text-[#CCFF00] drop-shadow-sm" : ""} 
                strokeWidth={isActive ? 2.5 : 2}
              />
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

function AppContent() {
  const [clubs, setClubs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [standings, setStandings] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [results, setResults] = useState([]);
  const [pendingResults, setPendingResults] = useState([]);
  const [adminSettings, setAdminSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Pick a random background image index per session
  // Injects a <style> tag to override html background - no DOM manipulation
  useEffect(() => {
    const bgImages = adminSettings?.background_images;
    const styleId = 'ketley-bg-style';
    if (!bgImages || bgImages.length === 0) {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
      return;
    }
    const SESSION_KEY = 'ketley_bg_index';
    let idx = sessionStorage.getItem(SESSION_KEY);
    if (idx === null) {
      idx = Math.floor(Math.random() * bgImages.length);
      sessionStorage.setItem(SESSION_KEY, String(idx));
    }
    const path = bgImages[parseInt(idx)] || bgImages[0];
    const url = `${API}/images/${path.replace('ketley-league/', '')}`;
    
    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = `html { background-image: url('${url}') !important; background-size: cover !important; background-position: center !important; background-attachment: fixed !important; }`;
  }, [adminSettings?.background_images]);

  // Fetch all data with cache busting
  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [clubsRes, seasonsRes, settingsRes] = await Promise.all([
        apiClient.get('/clubs'),
        apiClient.get('/seasons'),
        apiClient.get('/admin/settings')
      ]);

      setClubs(clubsRes.data);
      setSeasons(seasonsRes.data);
      setAdminSettings(settingsRes.data);

      const active = seasonsRes.data.find(s => s.is_active);
      if (active) {
        setActiveSeason(active);
        setSelectedSeason(active);
      } else if (seasonsRes.data.length > 0) {
        setSelectedSeason(seasonsRes.data[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch season-specific data
  const fetchSeasonData = useCallback(async (seasonId) => {
    try {
      const [standingsRes, fixturesRes, resultsRes, pendingRes] = await Promise.all([
        apiClient.get(`/standings?season_id=${seasonId}`),
        apiClient.get(`/fixtures?season_id=${seasonId}`),
        apiClient.get(`/results?season_id=${seasonId}`),
        apiClient.get(`/pending-results?season_id=${seasonId}`)
      ]);

      setStandings(standingsRes.data);
      setFixtures(fixturesRes.data);
      setResults(resultsRes.data);
      setPendingResults(pendingRes.data);
      setLastRefresh(Date.now());
    } catch (error) {
      console.error("Error fetching season data:", error);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Fetch season data when selected season changes
  useEffect(() => {
    if (selectedSeason) {
      fetchSeasonData(selectedSeason.id);
    }
  }, [selectedSeason, fetchSeasonData]);

  // Auto-refresh every 30 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedSeason) {
        fetchSeasonData(selectedSeason.id);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedSeason, fetchSeasonData]);

  const refreshData = useCallback(() => {
    fetchAllData();
    if (selectedSeason) {
      fetchSeasonData(selectedSeason.id);
    }
  }, [fetchAllData, fetchSeasonData, selectedSeason]);

  const value = {
    clubs,
    seasons,
    activeSeason,
    selectedSeason,
    setSelectedSeason,
    standings,
    fixtures,
    results,
    pendingResults,
    adminSettings,
    loading,
    refreshData,
    fetchSeasonData,
    lastRefresh
  };

  return (
    <AppContext.Provider value={value}>
      <div className="app-container" style={{ backgroundColor: '#ffffff', boxShadow: '0 0 60px rgba(0,0,0,0.3)' }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/fixtures" element={<FixturesPage />} />
          <Route path="/enter-result" element={<EnterResultPage />} />
          <Route path="/info" element={<InfoPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
        <BottomNav />
        <Toaster position="top-center" richColors />
      </div>
    </AppContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
